import { useEffect, useState } from "react";
import { GetAllTransactionsUseCase } from "../../../domain/usecase/transaction/getAll/UseCase";
import { useSnackbar } from "notistack";
import { Transaction } from "../../../domain/model/Transaction";
import { State } from "../../../domain/model/ResponseState";
import { GetAllOrdersUseCase } from "../../../domain/usecase/order/getAll/UseCase";
import { Order, UpdateOrderModel } from "../../../domain/model/Order";
import { UpdateOrderUseCase } from "../../../domain/usecase/order/update/UseCase";
import { downloadBlob, showGlobalLoadingView } from "../../../core/utils/Utils";
import { DeliverOrderUseCase } from "../../../domain/usecase/order/deliver/UseCase";
import { DownloadMultipleOrderReceiptsCase } from "../../../domain/usecase/order/downloadMultipleReceipts/UseCase";

export function OrdersViewModel(
  getAllOrdersUseCase: GetAllOrdersUseCase,
  updateOrderUseCase: UpdateOrderUseCase,
  downloadMultipleOrderReceiptsCase: DownloadMultipleOrderReceiptsCase,
  deliverOrderUseCase: DeliverOrderUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [orders, setOrders] = useState<Array<Order> | undefined>([]);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);

  const [skip, setSkip] = useState(0);
  const limit = 40;

  useEffect(() => {
    getOrders();
  }, []);

  async function getOrders() {
    setLoading(true);
    const response = await getAllOrdersUseCase.invoke(skip, limit);
    setLoading(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        if (response.data.length < limit) setShowLoadMoreButton(false);
        else setShowLoadMoreButton(true);
        setOrders(orders?.concat(response.data));
        setSkip(skip + limit);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function updateOrder(id: string, model: UpdateOrderModel) {
    showGlobalLoadingView(true);
    const response = await updateOrderUseCase.invoke(id, model);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        setOrders((prev) =>
          prev
            ? prev.map((order) =>
                order._id === id && model.status !== undefined
                  ? { ...order, status: model.status }
                  : order
              )
            : prev
        );
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function downloadMultipleOrderReceipts() {
    const selectedOrderIds = orders
      ?.filter((o) => o.selected)
      .flatMap((o) => o.orderId.toString());
    if (!selectedOrderIds || selectedOrderIds?.length == 0) {
      showSnackbar("No order was chosen");
      return;
    }
    showGlobalLoadingView(true);
    const response = await downloadMultipleOrderReceiptsCase.invoke(
      selectedOrderIds
    );
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        downloadBlob(response.data, `receipts_${Date.now()}.zip`);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function deliverOrder(id: string) {
    showGlobalLoadingView(true);
    const response = await deliverOrderUseCase.invoke(id);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(
          response.data?.message || "Order dispatched to NBOX successfully!",
          "success"
        );
        if (response.data?.deliveryInfo) {
          const deliveryInfo = response.data.deliveryInfo;
          setOrders((prev) =>
            prev
              ? prev.map((order) =>
                  order._id === id
                    ? { ...order, deliveryInfo }
                    : order
                )
              : prev
          );
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function selectUnselectOrder(orderId: string) {
    setOrders((prev) =>
      prev
        ? prev.map((order) =>
            order._id === orderId
              ? { ...order, selected: !order.selected }
              : order
          )
        : prev
    );
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }
  return {
    orders,
    isLoading,
    showLoadMoreButton,
    getOrders,
    updateOrder,
    deliverOrder,
    selectUnselectOrder,
    downloadMultipleOrderReceipts,
  };
}
